import { computed, nextTick, onBeforeUnmount, ref, watch, type CSSProperties, type Ref } from 'vue'
import { useUiConfig } from '../components/config-provider/context'
import { useOverlayFocusTrap } from './useOverlayFocusTrap'

export type PopupPlacement = 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'
export interface PopupOptions {
  placement?: () => PopupPlacement
  getPopupContainer?: () => ((triggerNode: HTMLElement) => HTMLElement) | undefined
  matchWidth?: boolean
  maxHeight?: number
  close: () => void
  onEscape?: () => void
  trapFocus?: boolean
  autoFocus?: boolean
}

/** Position and lifetime for anchored panels, shared by selects, pickers and popovers. */
export function usePopup(open: Readonly<Ref<boolean>>, trigger: Ref<HTMLElement | null>, panel: Ref<HTMLElement | null>, options: PopupOptions) {
  const config = useUiConfig()
  const container = computed(() => {
    if (typeof document === 'undefined') return undefined
    const getContainer = options.getPopupContainer?.() ?? config.value.getPopupContainer
    return getContainer && trigger.value ? getContainer(trigger.value) : document.body
  })
  const { zIndex } = useOverlayFocusTrap(open, panel, {
    trigger, trapFocus: options.trapFocus ?? false, autoFocus: options.autoFocus ?? false, restoreFocus: false,
    zIndexToken: '--rr-z-dropdown',
    closeOnEscape: () => { options.close(); (options.onEscape ?? (() => trigger.value?.focus({ preventScroll: true })))() },
    closeOnOutside: options.close, closeOnTab: options.close
  })
  const placement = ref<PopupPlacement>('bottomLeft')
  const position = ref<CSSProperties>({ visibility: 'hidden' })
  const fixed = computed(() => typeof document !== 'undefined' && (container.value === document.body || container.value === document.documentElement))
  const style = computed<CSSProperties>(() => ({ ...position.value, position: fixed.value ? 'fixed' : 'absolute', zIndex: zIndex.value }))
  let resizeObserver: ResizeObserver | undefined
  const margin = 8
  const gap = 4
  function updatePosition() {
    if (!open.value || !trigger.value || !panel.value) return
    const rect = trigger.value.getBoundingClientRect()
    const popup = panel.value.getBoundingClientRect()
    const viewWidth = document.documentElement.clientWidth || window.innerWidth
    const viewHeight = document.documentElement.clientHeight || window.innerHeight
    const maxHeight = Math.min(options.maxHeight ?? viewHeight, viewHeight - margin * 2)
    const width = Math.min(options.matchWidth ? rect.width : popup.width, viewWidth - margin * 2)
    const height = Math.min(popup.height, maxHeight)
    const preferred = options.placement?.() ?? 'bottomLeft'
    let side = preferred.startsWith('top') ? 'top' : preferred.startsWith('bottom') ? 'bottom' : preferred
    if (side === 'bottom' && viewHeight - rect.bottom < height + gap && rect.top > viewHeight - rect.bottom) side = 'top'
    else if (side === 'top' && rect.top < height + gap && viewHeight - rect.bottom > rect.top) side = 'bottom'
    else if (side === 'right' && viewWidth - rect.right < width + gap && rect.left > viewWidth - rect.right) side = 'left'
    else if (side === 'left' && rect.left < width + gap && viewWidth - rect.right > rect.left) side = 'right'
    let left = rect.left + (rect.width - width) / 2
    let top = rect.top + (rect.height - height) / 2
    if (side === 'top' || side === 'bottom') {
      top = side === 'top' ? rect.top - height - gap : rect.bottom + gap
      if (preferred.endsWith('Left')) left = rect.left
      if (preferred.endsWith('Right')) left = rect.right - width
    } else left = side === 'left' ? rect.left - width - gap : rect.right + gap
    left = Math.max(margin, Math.min(left, viewWidth - width - margin))
    top = Math.max(margin, Math.min(top, viewHeight - height - margin))
    if (!fixed.value) {
      const parent = panel.value.offsetParent as HTMLElement | null
      if (parent && parent !== document.body) {
        const parentRect = parent.getBoundingClientRect()
        left += parent.scrollLeft - parentRect.left - parent.clientLeft
        top += parent.scrollTop - parentRect.top - parent.clientTop
      } else { left += window.scrollX; top += window.scrollY }
    }
    placement.value = side as PopupPlacement
    position.value = { top: `${top}px`, left: `${left}px`, right: 'auto', bottom: 'auto', transform: 'none', width: options.matchWidth ? `${width}px` : undefined, maxWidth: `${viewWidth - margin * 2}px`, maxHeight: `${maxHeight}px`, visibility: 'visible' }
  }
  function cleanup() {
    window.removeEventListener('resize', updatePosition)
    window.removeEventListener('scroll', updatePosition, true)
    resizeObserver?.disconnect()
    resizeObserver = undefined
  }
  watch([open, container], async ([value]) => {
    if (typeof window === 'undefined') return
    cleanup()
    if (!value) { position.value = { visibility: 'hidden' }; return }
    await nextTick()
    if (!open.value) return
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updatePosition)
      if (trigger.value) resizeObserver.observe(trigger.value)
      if (panel.value) resizeObserver.observe(panel.value)
    }
  }, { immediate: true, flush: 'post' })
  onBeforeUnmount(() => { if (typeof window !== 'undefined') cleanup() })
  return { container, style, placement, updatePosition }
}
